import { useEffect, useRef, useState } from 'react';
import { Alert } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation, useRoute } from '@react-navigation/native';
import lodashIsEmpty from 'lodash/isEmpty';

import { MODALPROMPT } from '../Components/Web/Modal/ModalPrompt/config';
import useModalPrompt from '../Components/Web/Modal/ModalPrompt/hooks/useModalPrompt';
import constants from '../Config/constants';
import messages from '../Config/messages';
import { isUserDetailsIncomplete } from '../Helper';
import routeList from '../Routes/list';
import { setCart } from '../RTK/cart';
import { setForceLogout } from '../RTK/user';
import {
  isGuestSelector,
  isLoggedInSelector,
  userSelector,
} from '../RTK/user/selectors';
import { setGettingUserData } from '../RTK/utility';
import Service from '../Service';

import useSetSpecificUserData from './useSetSpecificUserData';
import useShouldCallApi from './useShouldCallApi';

export default function () {
  const alreadyShowned = useRef();
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const route = useRoute();
  const shouldCallApi = useShouldCallApi();
  const { showModalPrompt } = useModalPrompt();
  const { setCartData, setUserAddresses, setUserDetails } =
    useSetSpecificUserData();
  const isMounted = useRef();

  const isGuest = useSelector(isGuestSelector);
  const isLoggedIn = useSelector(isLoggedInSelector);
  const user = useSelector(userSelector);
  const rememberEmail = useSelector((state) => state.noSession?.rememberEmail);

  const [done, setDone] = useState(!isLoggedIn);
  const [isSetUserDataDone, setDoneSettingUserData] = useState(false);

  useEffect(() => {
    if (!done) {
      _setUserData();
    }
  }, []);

  useEffect(() => {
    // if user doesn't have address yet, it will redirect the screen to address adding
    // then when they update their address this will be called and done will become true
    if (isGuest || (isSetUserDataDone && user?.addresses?.length !== 0)) {
      setDone(true);
    }
  }, [user?.addresses]);

  // hooks for showing complete profile prompt
  useEffect(() => {
    if (
      !isGuest && // not guest user
      done && // if process is done
      !alreadyShowned.current && // if not yet showned
      !lodashIsEmpty(user) && // has user object
      isUserDetailsIncomplete(user) // if profile is incomplete
    ) {
      _showCompleteProfilePrompt();
    }
  }, [done, user]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const _showCompleteProfilePrompt = async () => {
    alreadyShowned.current = true; // to prevent multiple open when updating user address
    await new Promise((resolve) => setTimeout(resolve, 1500));

    if (constants.isWeb) {
      showModalPrompt(MODALPROMPT.completeInformation, {
        navigation: navigation,
      });
      return;
    }
    Alert.alert(
      'Complete your profile',
      'Would you like to complete your profile now?',
      [
        {
          text: 'Yes',
          onPress: () => {
            navigation.navigate(routeList.ACCOUNT_TAB);
            navigation.navigate(routeList.PROFILE);
          },
        },
        { text: 'Not now', style: 'cancel' },
      ],
    );
  };

  const _forceLogout = () => {
    dispatch(setCart([])); // clear cart
    dispatch(setForceLogout()); // force logout on the UI without logout api call
    if (constants.isWeb) {
      // on web, wait a little to propagate the dispatched on the redux or else it will cause infininte loading
      setTimeout(() => {
        window.location.href = routeList.HOME_TAB;
      }, 100);
    } else {
      // on mobile, redirect to auth screen and auto fill the information
      const phoneNumber = user?.phone || ''; // store phone number, because when logout it will be removed on redux state
      navigation.navigate(routeList.AUTH, {
        phone_number: phoneNumber.replace('+63', ''), // with existing phone number (replace +63 since we dont need it on the otp screen)
        remember: rememberEmail, // and existing remember flag
        session_expired: true, // also add session_expired flag
      });
    }
  };

  const _setUserData = async () => {
    if (!shouldCallApi && user?.addresses?.length === 0) {
      // user is guest/social and no saved address yet, redirect to address selection
      console.log(
        'Login as guest/social',
        'No address: redirect on address adding screen',
      );
      navigation.navigate(routeList.ADDRESS_SELECTOR);
      navigation.navigate(routeList.ADDRESS_ADD_EDIT);
    } else if (shouldCallApi) {
      // adding the loader flag for getting user details, addresses and carts
      await dispatch(setGettingUserData({ value: { loading: true } }));

      const detailsResponse = await setUserDetails();
      const addressesResponse = await setUserAddresses();
      const cartResponse = await setCartData();

      const responseStatuses = [
        detailsResponse?.status,
        addressesResponse?.status,
        cartResponse?.status,
      ];
      if (responseStatuses.includes(401) || responseStatuses.includes(403)) {
        if (constants.isWeb) {
          _forceLogout();
        } else {
          const alert = messages.SESSION_EXPIRED;
          Alert.alert(alert.title, alert.message, [
            { text: 'ok', onPress: _forceLogout },
          ]);
        }
      } else if (
        detailsResponse?.error ||
        addressesResponse?.error ||
        cartResponse?.error
      ) {
        // if any of the api call above encounter any error
        const errors = [
          detailsResponse?.error,
          addressesResponse?.error,
          cartResponse?.error,
        ];
        Alert.alert(
          'Error trying to get user data.\nPlease try again',
          Service.handleErrorMessage(errors),
        );
      }
      // if noAddress is returned on setUserAddresses it means use doesn't have they first address yet
      if (addressesResponse?.noAddress) {
        if (!constants.isWeb) {
          // so lets redirect it to address adding screen
          navigation.navigate(routeList.ADDRESS_SELECTOR);
          navigation.navigate(routeList.ADDRESS_ADD_EDIT);
        } else {
          showModalPrompt(MODALPROMPT.addressDetails, {
            status: 'new',
            selectedAddress: {},
            navigation: navigation,
            route: route,
          });
        }
      } else if (isMounted.current) {
        // if no error occur and noAddress is not returned from setUserAddresses setDone as true
        setDone(true);
      }
      await dispatch(setGettingUserData({ value: { loading: false } })); // removing the loader flag for getting user details
    }

    // flag for done processing the setting of user data
    if (isMounted.current) {
      setDoneSettingUserData(true);
    }
  };

  return done;
}
